import { useEffect, useState } from "react";

const SAMPLE = 96;
const MARGIN = 0.04;
const STORAGE_KEY = "hsl.logoInfo.v2";

const toHex = (r, g, b) => `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;

export function analysePixels(data, w, h) {
  let minX = w, minY = h, maxX = -1, maxY = -1;
  let opaque = 0, ink = 0, strong = 0;
  const buckets = new Map();
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      if (data[i + 3] <= 16) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      opaque += 1;
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      ink += (data[i + 3] / 255) * (1 - lum);
      if (data[i + 3] > 128 && lum < 0.5) strong += 1;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const sat = max === 0 ? 0 : (max - min) / max;
      if (sat > 0.35 && max > 60 && max < 250) {
        const key = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
        const e = buckets.get(key) || { n: 0, r: 0, g: 0, b: 0 };
        e.n += 1; e.r += r; e.g += g; e.b += b;
        buckets.set(key, e);
      }
    }
  }
  if (maxX < 0) return null;
  let best = null;
  buckets.forEach((e) => { if (!best || e.n > best.n) best = e; });
  const color = best && best.n > opaque * 0.03 ? toHex(best.r / best.n, best.g / best.n, best.b / best.n) : null;
  return { box: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 }, light: ink / opaque < 0.3 && strong / opaque < 0.15, color };
}

const readStore = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

// url → { box (fractions of the image), aspect, light, color }, kept across visits.
const known = typeof window !== "undefined" ? readStore() : {};
const pending = new Map();
let saveTimer = null;
const persist = () => {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(known));
    } catch {
      // Storage full or disabled: the in-memory copy still works for this visit.
    }
  }, 500);
};

const withSrc = (url, meta) => (meta ? { src: url, ...meta } : null);

const analyse = (url) => {
  if (known[url]) return Promise.resolve(withSrc(url, known[url]));
  if (pending.has(url)) return pending.get(url);
  const job = new Promise((resolve) => {
    const fallback = { src: url, box: null, aspect: 1, light: false, color: null };
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onerror = () => resolve(fallback);
    img.onload = () => {
      try {
        const nw = img.naturalWidth || 1;
        const nh = img.naturalHeight || 1;
        const scale = Math.min(1, SAMPLE / Math.max(nw, nh));
        const w = Math.max(1, Math.round(nw * scale));
        const h = Math.max(1, Math.round(nh * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const info = analysePixels(ctx.getImageData(0, 0, w, h).data, w, h);
        if (!info) return resolve(fallback);
        const meta = {
          box: { x: info.box.x / w, y: info.box.y / h, w: info.box.w / w, h: info.box.h / h },
          aspect: nw / nh,
          light: info.light,
          color: info.color,
        };
        known[url] = meta;
        persist();
        resolve(withSrc(url, meta));
      } catch {
        resolve(fallback);
      }
    };
    img.src = url;
  });
  pending.set(url, job);
  return job;
};

export const loadLogoInfo = (url) => (url ? analyse(url) : Promise.resolve(null));

/**
 * Absolute-position style that scales and offsets the original image so its artwork
 * fills a square container (minus a small margin). Percentages are of the container.
 */
export const cropStyle = (info, margin = MARGIN) => {
  if (!info?.box) return { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" };
  const { box, aspect } = info;
  const bw = box.w * aspect;
  const bh = box.h;
  const k = (1 - 2 * margin) / Math.max(bw, bh);
  return {
    position: "absolute",
    width: `${aspect * k * 100}%`,
    height: `${k * 100}%`,
    left: `${((1 - bw * k) / 2 - box.x * aspect * k) * 100}%`,
    top: `${((1 - bh * k) / 2 - box.y * k) * 100}%`,
    maxWidth: "none",
    maxHeight: "none",
  };
};

// Logos ship with wildly different transparent padding; cropping it makes crests read at the same size.
export function useLogoInfo(url) {
  const [info, setInfo] = useState(() => (url && known[url] ? withSrc(url, known[url]) : null));
  useEffect(() => {
    if (!url) return undefined;
    if (known[url]) {
      setInfo((cur) => (cur?.src === url ? cur : withSrc(url, known[url])));
      return undefined;
    }
    let active = true;
    setInfo(null);
    analyse(url).then((r) => active && setInfo(r));
    return () => {
      active = false;
    };
  }, [url]);
  return url ? info : null;
}
